import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import '../styles/Inventory.css';

const Inventory = () => {
  const [allItems, setAllItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showScanner, setShowScanner] = useState(false);

  // Filters
  const [sourceFilter, setSourceFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [inventoryRes, itemsRes, customersRes, categoriesRes] = await Promise.all([
        api.get('/inventory'),
        api.get('/inventory/all-items'),
        api.get('/customers'),
        api.get('/categories')
      ]);
      setTotals(inventoryRes.data.totals);
      setAllItems(itemsRes.data);
      setCustomers(customersRes.data);
      setCategories(categoriesRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const labels = useMemo(() => {
    const set = new Set();
    allItems.forEach(i => { if (i.label_text) set.add(i.label_text); });
    return Array.from(set).sort();
  }, [allItems]);

  const filteredItems = allItems.filter(item => {
    const matchesSource = !sourceFilter ||
      (sourceFilter === 'pending' ? (item.source === 'shipment' && item.shipment_status === 'pending') :
       sourceFilter === 'shipped' ? (item.source === 'shipment' && item.shipment_status === 'shipped') :
       item.source === sourceFilter);

    const matchesSearch = !searchTerm || (() => {
      const text = [item.item_name, item.part_number, item.source_number, item.customer_name, item.lot_number, item.category_name, item.label_text].join(' ').toLowerCase();
      const qtyStr = item.qty_per_case ? String(item.qty_per_case) : '';
      const words = searchTerm.toLowerCase().trim().split(/\s+/);
      if (words.length === 1) return text.includes(words[0]) || qtyStr === words[0];
      return words.every(word => text.includes(word) || qtyStr === word);
    })();

    const matchesCustomer = !customerFilter || item.customer_id === parseInt(customerFilter);
    const matchesCategory = !categoryFilter || item.category_id === parseInt(categoryFilter);
    const matchesLabel = !labelFilter ||
      (labelFilter === '__none__' ? !item.label_text :
       labelFilter === '__has__' ? !!item.label_text :
       item.label_text === labelFilter);

    return matchesSource && matchesSearch && matchesCustomer && matchesCategory && matchesLabel;
  });

  // Search totals banner — groups filtered items by name and sums quantities
  const searchTotals = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return null;

    const itemTotals = {};
    filteredItems.forEach(item => {
      const key = item.item_name;
      if (!itemTotals[key]) {
        itemTotals[key] = {
          name: item.item_name, partNumber: item.part_number,
          totalQty: 0, totalWeight: 0,
          sources: { pallet: new Set(), wheel: new Set(), shipment: new Set() }
        };
      }
      itemTotals[key].totalQty += parseFloat(item.quantity) || 0;
      itemTotals[key].totalWeight += parseFloat(item.subtotal_weight) || 0;
      itemTotals[key].sources[item.source].add(item.source_id);
    });

    const result = Object.values(itemTotals)
      .map(t => ({
        ...t,
        pallets: t.sources.pallet.size,
        wheels: t.sources.wheel.size,
        shipments: t.sources.shipment.size
      }))
      .sort((a, b) => b.totalQty - a.totalQty);

    return result.length > 0 ? result : null;
  }, [searchTerm, filteredItems]);

  const handleBarcodeScan = (barcode) => {
    setSearchTerm(barcode);
    setShowScanner(false);
  };

  const sourceLabel = { pallet: 'Pallet', wheel: 'Wheel', shipment: 'Shipment', pending: 'Pending Shipment', shipped: 'Shipped Shipment' };

  if (loading) {
    return (
      <div className="inventory-page">
        <div className="loading">Loading inventory...</div>
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <div className="page-header">
        <div className="header-top">
          <h1>Inventory</h1>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e3f2fd' }}>📦</div>
          <div className="stat-content">
            <div className="stat-value">{totals.totalPallets || 0}</div>
            <div className="stat-label">Active Pallets</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e8f5e9' }}>📋</div>
          <div className="stat-content">
            <div className="stat-value">{allItems.length}</div>
            <div className="stat-label">Total Item Rows</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fff3e0' }}>⚖️</div>
          <div className="stat-content">
            <div className="stat-value">
              {allItems.reduce((s, i) => s + parseFloat(i.subtotal_weight || 0), 0).toFixed(1)}
            </div>
            <div className="stat-label">Total Weight (kg)</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by item, source #, customer, lot..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="scan-btn" onClick={() => setShowScanner(true)} title="Scan barcode">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
            </svg>
          </button>
        </div>
        <div className="toolbar-filters">
          <select className="filter-select" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All Sources</option>
            <option value="pallet">Pallets</option>
            <option value="wheel">Wheels</option>
            <option value="pending">Pending Shipment</option>
            <option value="shipped">Shipped</option>
          </select>
          <select className="filter-select" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
            <option value="">All Customers</option>
            {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <select className="filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
          </select>
          <select className="filter-select" value={labelFilter} onChange={(e) => setLabelFilter(e.target.value)}>
            <option value="">All Labels</option>
            <option value="__has__">Has Label</option>
            <option value="__none__">No Label</option>
            {labels.map((l) => (<option key={l} value={l}>{l}</option>))}
          </select>
        </div>
      </div>

      {/* Search totals banner */}
      {searchTotals && (
        <div className="search-totals-banner">
          <div className="search-totals-title">
            Items matching "{searchTerm}"{sourceFilter ? ` in ${sourceLabel[sourceFilter]}s` : ''}:
          </div>
          <div className="search-totals-list">
            {searchTotals.slice(0, 5).map((item, idx) => (
              <div key={idx} className="search-total-item">
                <span className="item-name">{item.name}</span>
                <span className="item-stats">
                  <strong>{item.totalQty.toFixed(0)}</strong> cases
                  <span className="separator">•</span>
                  <strong>{item.totalWeight.toFixed(1)}</strong> kg
                  {item.pallets > 0 && <><span className="separator">•</span>{item.pallets} pallet{item.pallets !== 1 ? 's' : ''}</>}
                  {item.wheels > 0 && <><span className="separator">•</span>{item.wheels} wheel{item.wheels !== 1 ? 's' : ''}</>}
                  {item.shipments > 0 && <><span className="separator">•</span>{item.shipments} shipment{item.shipments !== 1 ? 's' : ''}</>}
                </span>
              </div>
            ))}
            {searchTotals.length > 5 && (
              <div className="search-totals-more">+{searchTotals.length - 5} more items...</div>
            )}
          </div>
        </div>
      )}

      {/* Items table or empty state */}
      {filteredItems.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <p className="empty-text">No items found</p>
          <p className="empty-subtext">
            {searchTerm || sourceFilter || customerFilter || categoryFilter || labelFilter
              ? 'Try adjusting your filters' : 'Items will appear here from pallets, wheels, and shipments'}
          </p>
        </div>
      ) : (
        <div className="inventory-items-container">
          <table className="inventory-items-table full-width">
            <thead>
              <tr>
                <th>Part #</th>
                <th>Item Name</th>
                <th>Category</th>
                <th>Qty</th>
                <th>Weight</th>
                <th>Lot #</th>
                <th>Label</th>
                <th>Source</th>
                <th>Customer</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={`${item.source}-${item.id}`}>
                  <td className="part-number">{item.part_number}</td>
                  <td>{item.item_name}</td>
                  <td>{item.category_name || '-'}</td>
                  <td>{item.quantity}</td>
                  <td>{parseFloat(item.subtotal_weight || 0).toFixed(2)} kg</td>
                  <td className="lot-number">{item.lot_number || '-'}</td>
                  <td>
                    {item.label_text ? (
                      <span className="label-badge" style={{ backgroundColor: item.label_color || '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                        {item.label_text}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    <a
                      href={`/${item.source === 'pallet' ? 'pallets' : item.source === 'wheel' ? 'wheels' : 'shipments'}?id=${item.source_id}`}
                      className={`source-badge ${item.source}`}
                    >
                      {item.source_number}
                    </a>
                  </td>
                  <td>{item.customer_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="items-summary">
            Showing {filteredItems.length} of {allItems.length} items
            {sourceFilter ? ` from ${sourceLabel[sourceFilter]}s` : ' across all sources'}
          </div>
        </div>
      )}

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Barcode"
      />
    </div>
  );
};

export default Inventory;
